import { useMutation } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { optimizePrompt } from "@/api/studio-ai-api"
import { Film, ImageIcon, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"

type PromptStepProps = {
  mediaType: MediaType
  prompt: string
  onPromptChange: (value: string) => void
  maxLength?: number
  size?: string
  duration?: number
  aspectRatio?: string
  style?: string
}

export function PromptStep({
  mediaType,
  prompt,
  onPromptChange,
  maxLength = 2000,
  size,
  duration,
  aspectRatio,
  style,
}: PromptStepProps) {
  const isVideo = mediaType === MediaType.Video
  const keyPrefix = isVideo ? "studioAi.video.prompt" : "studioAi.image.prompt"
  const { t } = useTranslation(undefined, { keyPrefix })

  const optimizeMutation = useMutation({
    mutationFn: () =>
      optimizePrompt({
        prompt,
        media_type: isVideo ? "video" : "image",
        size,
        duration,
        aspect_ratio: aspectRatio,
        style,
      }),
    onSuccess: (data) => {
      onPromptChange(data.optimized_prompt)
      toast.success(t("optimizeSuccess"))
    },
    onError: () => toast.error(t("optimizeError")),
  })

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium" htmlFor="wizard-prompt">
            <span className="flex items-center gap-2">
              {isVideo ? <Film className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
              {t("label")}
            </span>
          </Label>
          <div className="flex items-center gap-3">
            <Button
              disabled={!prompt.trim() || optimizeMutation.isPending}
              size="sm"
              variant="outline"
              onClick={() => optimizeMutation.mutate()}
            >
              {optimizeMutation.isPending ? (
                <Spinner className="mr-1 h-3.5 w-3.5" />
              ) : (
                <Sparkles className="mr-1 h-3.5 w-3.5" />
              )}
              {optimizeMutation.isPending ? t("optimizing") : t("optimize")}
            </Button>
            <span className="text-muted-foreground text-xs">
              {prompt.length} / {maxLength}
            </span>
          </div>
        </div>

        <Textarea
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="min-h-[200px] resize-none text-base"
          disabled={optimizeMutation.isPending}
          id="wizard-prompt"
          maxLength={maxLength}
          placeholder={t("placeholder")}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
        />

        <p className="text-muted-foreground text-xs">{t("tip")}</p>
      </div>
    </div>
  )
}
